// The control-event bus (P2.8) — Architecture §3, "the spine, and the MIDI seam".
//
// §3: "Input sources (keyboard, pointer; MIDI later) produce raw events; a **mapping layer**
// (user-editable, persisted) translates them into `ControlEvent { id, value, timestamp, source }`.
// Consumers subscribe by id or namespace."
//
// **Why this exists rather than components calling the deck directly.** P2.7's buttons did call the
// deck directly, and it worked — for one source. P2.8 adds the second, and the whole value of §3 is
// that the second source costs nothing: a keyboard, and at FF-1 a MIDI controller, must emit the
// *same* events into the *same* mapping layer. §3 spells out the four things that buys — the MIDI
// seam, FR-39's remapping, the keyboard's rollover logic living in one place, and `ControlEvent`
// streams being recordable by construction for a future macro feature.
//
// P2.8's rule states the test of whether this is real: "if [the keyboard] needs a private path the
// seam is wrong." So the pointer was moved onto this bus in the same commit. Two sources, one path.
//
// Deliberately tiny. No priorities, no async, no event queue: Architecture §3 puts the control plane
// on "ordinary event delivery on the main thread", and the sound-affecting half is what crosses into
// the ring. A bus that grew a scheduler would be competing with the ring for a job the ring already
// does better.

import {
  controlIdInNamespace,
  type ControlEvent,
  type ControlId,
} from '../platform/types/control';

export type ControlHandler = (event: ControlEvent) => void;

interface Subscription {
  /** An exact id, or a namespace prefix like `deck.a`. */
  readonly pattern: string;
  readonly handler: ControlHandler;
}

/** Undo a subscription. Returned rather than requiring the handler back, so an anonymous arrow can
 * be unsubscribed — which is what every React effect wants. */
export type Unsubscribe = () => void;

export class ControlBus {
  readonly #subscriptions = new Set<Subscription>();

  /**
   * Subscribe to one id or a whole namespace.
   *
   * `deck.a` matches `deck.a.play` and `deck.a.cue` but **not** `deck.ab.play` —
   * `controlIdInNamespace` matches on segment boundaries for exactly that reason. A plain
   * `startsWith` would silently swallow the neighbouring deck the day someone names one `ab`.
   */
  subscribe(pattern: string, handler: ControlHandler): Unsubscribe {
    const subscription: Subscription = { pattern, handler };
    this.#subscriptions.add(subscription);
    return () => {
      this.#subscriptions.delete(subscription);
    };
  }

  /**
   * Deliver an event to every matching subscriber.
   *
   * Iterates a copy, because a handler is allowed to subscribe or unsubscribe while being called —
   * a control that arms another control is an ordinary thing to want, and mutating the set mid-
   * iteration is how that turns into a bug nobody can reproduce.
   *
   * A throwing handler must not stop the others. One misbehaving consumer taking down transport is
   * the failure mode a bus exists to prevent, not to introduce.
   */
  publish(event: ControlEvent): void {
    for (const subscription of [...this.#subscriptions]) {
      if (!controlIdInNamespace(event.id, subscription.pattern)) {
        continue;
      }
      try {
        subscription.handler(event);
      } catch (cause: unknown) {
        console.error(`[synapse] control handler failed for ${event.id}`, cause);
      }
    }
  }

  /** How many subscribers are attached. Exists so a test can prove `subscribe` returned a working
   * unsubscribe rather than assuming it — a leaked subscription is invisible until it is not. */
  get subscriberCount(): number {
    return this.#subscriptions.size;
  }
}

/** Convenience for sources: build a well-formed event without repeating the shape. */
export function controlEvent(
  id: ControlId,
  value: number,
  timestamp: number,
  source: ControlEvent['source'],
): ControlEvent {
  return { id, value, timestamp: timestamp as ControlEvent['timestamp'], source };
}
